
import { WizFocusManager } from '../../../../services/core/focus/wiz-focus-manager';
import { Component, Input, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, Renderer2, forwardRef } from '@angular/core';
import { WizTextInput } from '../wiz-text-input/wiz-text-input';
import { BASE_COMPONENT, WizBaseComponent } from '../../../core/wiz-base-component';
import {
  WizValidatorFn,
  WizValidators,
  WizValidationError,
  WizValidationErrors
} from '../../../../services/validators/wiz-validators';
import { IWIZ_COMPONENT} from '../../../../interfaces/iwiz-component';
import { WIZ_ERROR_MESSAGE, WIZ_EVENT_TYPE, WizNumberUtils } from '@wizeoo/akm-core-lib';
import { WizEvent } from '../../../../events/wiz-event';
import { WizEventManager } from '../../../../events/wiz-event.manager';

@Component({
  selector: 'wiz-numeric-input',
  templateUrl: '../wiz-input.html',
  styleUrls: ['../wiz-input.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: BASE_COMPONENT, useExisting: forwardRef(() => WizNumericInput) },
    { provide: WizBaseComponent, useExisting: forwardRef(() => WizNumericInput) },
    { provide: IWIZ_COMPONENT, useExisting: forwardRef(() => WizNumericInput) }
  ]
})
export class WizNumericInput extends WizTextInput {

  constructor(protected cdref: ChangeDetectorRef, protected myDOM: ElementRef, public renderer: Renderer2) {
    super(cdref, myDOM, renderer);
    this.wizMaxlength = 50;// base 10.5 par défaut, géré par la regEx

    this.mode = 'numericInput';
  }

  // ####
  // ####
  // #### configuration ####
  // ####
  // ####

  /** alimentation par le number
   * Attention, ne faire qu'en externe. Cela impacte WizValue, donc aussi _initValue
   */
  @Input()
  public set wizNumber(n: number) {
    this.setWizNumber(n);
  }
  public get wizNumber(): number {
    return this.getWizNumber();
  }
  private _wizNumber: number = NaN;
  /**
   * methodes getter/setter surchargeables du WizNumber
   */
  protected setWizNumber(n: number) {
    // TO_VALIDATE:  before
    this._wizNumber = n;
    // this.setWizValue(isNaN(this._wizNumber) ? undefined : this._wizNumber.toString());

    // TO_VALIDATE: after
    // Utilisation d'une fonction utils pour déterminé si le _wizNumber est valid
    // console.log('setWizNumber', n);
    if (WizNumberUtils.isValidNumber(this._wizNumber)) {
      // this.setWizValue(this._wizNumber.toString());
      this.setWizValue(n);
      // console.log('setWizNumber isValidNumber', n, this._wizNumber.toString());
    } else {// chaine invalide, possible -,.  => A maintenir pour éviter double saisie
      // console.log('wizNumber invalid', this.innerInputValue);
      // this.setWizValue(this.innerInputValue);
      // console.log('setWizNumber invalid', n);
      this.setWizValue(null);
    }
  }
  protected getWizNumber(): number {
    // console.log('ni getWizNumber', this._wizNumber);
    return this._wizNumber;
  }

  /** borne max (<=) */
  @Input()
  public set wizMaxNumber(v: number) {
    this.setWizMaxNumber(v);
  }
  public get wizMaxNumber(): number {
    return this.getWizMaxNumber();
  }
  private _wizMaxNumber: number;
  /**
   * methodes getter/setter surchargeables du WizMaxNumber
   */
  protected setWizMaxNumber(v: number) {
    this._wizMaxNumber = v;
    this.cdref.markForCheck();
  }
  protected getWizMaxNumber(): number {
    return this._wizMaxNumber;
  }

  /** borne min (>=) */
  @Input()
  public set wizMinNumber(v: number) {
    this.setWizMinNumber(v);
  }
  public get wizMinNumber(): number {
    return this.getWizMinNumber();
  }
  private _wizMinNumber: number;
  /**
   * methodes getter/setter surchargeables du WizMinNumber
   */
  protected setWizMinNumber(v: number) {
    this._wizMinNumber = v;
    // contrôle de cohérence sur chiffre négatif
    if (this._wizMinNumber < 0) {
      this.allowNegative = true;
    }
    this.cdref.markForCheck();
  }
  protected getWizMinNumber(): number {
    return this._wizMinNumber;
  }

  /** nombre max de décimales (digits) */
  @Input()
  public set nbDecimal(v: number) {
    this.setNbDecimal(v);
  }
  public get nbDecimal(): number {
    return this.getNbDecimal();
  }
  private _nbDecimal: number = 0;
  /**
   * methodes getter/setter surchargeables du NbDecimal
   */
  protected setNbDecimal(v: number) {
    this._nbDecimal = v;
    this.cdref.markForCheck();
  }
  protected getNbDecimal(): number {
    return this._nbDecimal;
  }

  /** nombre max d'entier (digits) */
  @Input()
  public set nbInteger(v: number) {
    this.setNbInteger(v);
  }
  public get nbInteger(): number {
    return this.getNbInteger();
  }
  private _nbInteger: number = 10;
  /**
   * methodes getter/setter surchargeables du NbInteger
   */
  protected setNbInteger(v: number) {
    this._nbInteger = v;
    this.cdref.markForCheck();
  }
  protected getNbInteger(): number {
    return this._nbInteger;
  }

  /** autoriser les signes - */
  @Input()
  public set allowNegative(v: boolean) {
    this.setAllowNegative(v);
  }
  public get allowNegative(): boolean {
    return this.getAllowNegative();
  }
  private _allowNegative: boolean = true;
  /**
   * methodes getter/setter surchargeables du AllowNegative
   */
  protected setAllowNegative(v: boolean) {
    this._allowNegative = v;
    this.cdref.markForCheck();
  }
  protected getAllowNegative(): boolean {
    return this._allowNegative;
  }

  // ####
  // ####
  // #### mécanique interne ####
  // ####
  // ####



  /**
   * Numeric input restriction on key press
   * @param e Keyboard event
   */
  public onKeyPress(e: KeyboardEvent) {
    // Les caractères spéciaux ne génèrent pas de keypress
    this.preventInsert(e.key, e);
  }

  /**
   * appeler lors d'un coller dans l'input
   * cette méthode récupère le texte coller et appelle la méthode wizPreventInsert
   * On écoute l'évenement paste en plus du keydown ctrl + v || shift + Inser, car un coller est déclenché par le clavier ou la souris
   * @param event, Paste event
   */
  public onPaste(event: ClipboardEvent) {
    // le caractère en cours
    const inputChar = event.clipboardData.getData('Text').replace(',', '.');
    this.preventInsert(inputChar, event);
  }

  /**
   * Annule l'insertion d'un caractère ou chaine de caractère
   * Si cette valeur est invalide par rapport à numericRegExp
   * @params value, le caratère ou la chaine à inserer
   * @params event, l'event à prevent si value n'est pas autorisé
   */
  public preventInsert(value: string, event: Event) {
    // La chaine existante
    const currentString: string = this.innerInputValue;
    // la future chaine
    const selectionStart: number = this.wizInput.nativeElement.selectionStart;
    const selectionEnd: number = this.wizInput.nativeElement.selectionEnd;
    const nextString: string = currentString.substr(0, selectionStart) + value + currentString.substr(selectionEnd);
    // on teste
    if (nextString && !String(nextString).replace(',', '.').match(this.numericRegExp)) {
      event.preventDefault();
    }
  }

  /** surcharge du getWizValue pour formattage sur focus Outside */
  public getWizValue(): any {
    let result: any;
    if (WizFocusManager.getInstance().iHaveFocus(this)) {
      result = super.getWizValue();
    } else {
      result = this.formatedNumber;
    }
    return result;

  }

  /** retour la valeur formatée (rendu sur focus outside) */
  public get formatedNumber(): string {
    // Verify if separator is present to translation files
    // TO_VALIDATE : before
    let result: string = WizNumberUtils.formatNumber(this._wizNumber, this.nbDecimal);
    return result;

  }

  /** RegExp pour contrôle de saisie */
  private get numericRegExp(): RegExp {
    const regexStr: string = '^#ALLOW_NEG\\d{0,#N_LENGHT}#ALLOW_DECIMAL$'
      .replace('#ALLOW_NEG', this.allowNegative ? '-?' : '')
      .replace('#ALLOW_DECIMAL', this.nbDecimal > 0 ? '(\\.\\d{0,#N_DECIMAL})?' : '')
      .replace('#N_DECIMAL', this.nbDecimal > 0 ? this.nbDecimal.toString() : '')
      .replace('#N_LENGHT', String(this.nbInteger));
    return new RegExp(regexStr, 'g');
  }

  /** RegExp pour contrôler la saisi des caractères - . , */
  private initialSpecialCharacters = new RegExp('^[\,\.\-]$', 'g');

  /**
   * @Override
   * sur change de la valeur de saisie, répercuter sur _wizValue SANS déclencher d'init
   * @param value, chaine de caratère saisie dans l'input
   */
  public onChange(value: any) {
    // WARNING : ne pas utiliser le super() pour éviter le dispatch d'un event
    // sur le numeric input l'event est dispatcher sur le foucusOut
    // test si e est egale à l'un des caractères , - ou .
    if (`${value}`.match(this.initialSpecialCharacters)) {
      super.onChange(value);
    } else {
      // console.log('onChange', value, WizNumberUtils.wizParseFloat(value));
      this.wizNumber = WizNumberUtils.wizParseFloat(value);
    }

    // Compare la valeur d'entrée avec la valeur de sortie
    // pour déclecnher les boutons de sauvegarde sans avoir besoin de sortir du champ
    if (this._wizInitialValue !== this.formatedNumber) {
      this.wizKey.emit(`${this.wizValue}`);
    }
  }


  /**
   * contrat d'interface IwizError: traitement complémentaire pour rollBack
   * sur focusOut. Le retour à la dernière bonne conf est déjà géré par ErrorManager
   * LMA : ici, on force un "change" pour synchroniser _wizNumber, qui sert pour le formatteur
   */
  public wizRollback(): void {
    // console.log('wizRollback', 'numeric');
    // attention au undefined, sinon cast en 0

    this.wizNumber = WizNumberUtils.wizParseFloat(this._wizValue);
  }

  // ####
  // ####
  // #### contraintes ####
  // ####
  // ####/

  /**
   * Custom required validation that check wizNumber
   * @param errorMessage, the errormessage
   */
  protected getRequiredValidator(errorMessage: string): WizValidatorFn {
    return (compo: WizNumericInput): WizValidationErrors | null => {
      const temp: WizValidationError = {
        wizError: errorMessage
      };
      // TO_VALIDATE : before
      // return !isNaN(compo.wizNumber) ? null : [temp];

      // TO_VALIDATE: after
      // utilisation de la classe utils
      return WizNumberUtils.isValidNumber(compo.wizNumber) ? null : [temp];
    };
  }

  /** message d'erreur < valeur min */
  @Input()
  public set wizMinValueErrorMessage(v: string) {
    this.setWizMinValueErrorMessage(v);
  }
  public get wizMinValueErrorMessage(): string {
    return this.getWizMinValueErrorMessage();
  }
  private _wizMinValueErrorMessage: string = 'validators.numberValidator.lowerThanMinError';
  /**
   * methodes getter/setter surchargeables du WizMinValueErrorMessage
   */
  protected setWizMinValueErrorMessage(v: string) {
    this._wizMinValueErrorMessage = v;
    this.cdref.markForCheck();
  }
  protected getWizMinValueErrorMessage(): string {
    return this._wizMinValueErrorMessage;
  }
  /** message d'erreur < >valeur max */
  @Input()
  public set wizMaxValueErrorMessage(v: string) {
    this.setWizMaxValueErrorMessage(v);
  }
  public get wizMaxValueErrorMessage(): string {
    return this.getWizMaxValueErrorMessage();
  }
  private _wizMaxValueErrorMessage: string = 'validators.numberValidator.greaterThanMaxError';
  /**
   * methodes getter/setter surchargeables du WizMaxValueErrorMessage
   */
  protected setWizMaxValueErrorMessage(v: string) {
    this._wizMaxValueErrorMessage = v;
    this.cdref.markForCheck();
  }
  protected getWizMaxValueErrorMessage(): string {
    return this._wizMaxValueErrorMessage;
  }
  /** contrat d'interface ErrorManager */
  public getValidators(): WizValidatorFn[]  {
    const validators: WizValidatorFn[] = [];

    if (this.wizMandatory) {
      const errorMessage = this.wizRequiredErrorMessage ? this.wizRequiredErrorMessage : WIZ_ERROR_MESSAGE.REQUIRED;
      validators.push(this.getRequiredValidator(errorMessage));
    }

    // if wizMaxNumber is gived, the maxNumber validator in added to wizValidatorsArray
    if (WizNumberUtils.isValidNumber(this.wizMaxNumber)) {
      const errorParams = [this.wizMaxNumber];
      const errorMessage = this.wizMaxValueErrorMessage ? this.wizMaxValueErrorMessage : WIZ_ERROR_MESSAGE.MAX_NUMBER;
      validators.push(WizValidators.wizMax(this.wizMaxNumber, this.wizNumber, errorMessage, errorParams));
    }

    // if wizMaxNumber is gived, the maxNumber validator in added to wizValidatorsArray
    if (WizNumberUtils.isValidNumber(this.wizMinNumber)) {
      const errorParams = [this.wizMinNumber];
      const errorMessage = this.wizMinValueErrorMessage ? this.wizMinValueErrorMessage : WIZ_ERROR_MESSAGE.MIN_NUMBER;
      validators.push(WizValidators.wizMin(this.wizMinNumber, this.wizNumber, errorMessage, errorParams));
    }
    return validators;
  }

  public wizGetFocus(): void {
    super.wizGetFocus();

    // Initialisation _wizInitialValue a chaque prise de focus sur le composant
    // Set wizInitialValue pour éviter de dispatcher des événements CHANGE inutilement
    // l'événement change sera dispatché seulement si _wizInitialValue !=== _wizLastGoodValue
    // Utilisation de formatedNumber car le this.getValue() ne renvoie pas la même valeur en fonction du focus
    this._wizInitialValue = this.formatedNumber;
  }

  /**
   * donne le focus à l'input depuis FocusManager
   *
   * @param value valeur à injecter
   */
   public wizFocusInjector(value: string): void {
    if (!this.wizDisabled) {
      if (value && value.replace(',', '.').match(this.numericRegExp)) { // controle sur base de la regExp
        this.wizInput.nativeElement.select(); // select pour selectionner le contenu de l'input lors du focus
        this.onChange(value);
        //this.wizNumber = Number(value);
      } else {
        this.wizFocus();
        // console.log('wizFocusWithValue nope', this.innerInput.nativeElement.value);
      }
    }
  }


  /**
   * Override du wizLoseFocus
   * Permet de dispatcher une valeur quand la valeur a changée
   * Cette méthode est directement appelée via le focusManager
   */
  public wizLoseFocus(): void {
    super.wizLoseFocus();

    // Compare la valeur d'entrée avec la valeur de sortie
    // Utilisation de formatedNumber car le this.getValue() ne renvoie pas la même valeur en fonction du focus
    if (this._wizInitialValue !== this.formatedNumber) {
      WizEventManager.dispatchEvent(this.elementRef, new WizEvent(WIZ_EVENT_TYPE.CHANGE, this, this.wizValue));
      this.wizChange.emit(`${this.wizValue}`);
    }
  }

  /**
   * override de la methode pour annuler la valeur de l'input lorsqu'elle vaut - , ou .
   */
  public prepareForCheck(): void {
    if (!!this._wizValue && this._wizValue.toString().match(this.initialSpecialCharacters)) {
      this.wizNumber = null;
    }
  }

  /** Valeur de l'input interne */
  public getInnerInputValue(): string {
    return this.getWizValue();
  }

}
